import { EnvironmentId } from "@cmmd-forge/contracts";
import { type QueryClient } from "@tanstack/react-query";

import { getSavedEnvironmentRecord, type SavedEnvironmentRecord } from "./catalog";

import * as savedCloud from "./savedCloudEnvironmentLifecycle";
import { type EnvironmentConnection } from "./connection";

import {
  hasCloudRuntimeCredentialProvider,
  unregisterCloudRuntimeCredentialProvider,
} from "./cloudRuntimeCredentialAccess";

import { createEnvironmentServiceLifecycle } from "./environmentServiceLifecycle";
import {
  createPrimaryEnvironmentConnection as createPrimaryEnvironmentConnectionInternal,
  createDesktopLocalEnvironmentConnection as ensureDesktopLocalEnvironmentConnectionInternal,
} from "./environmentConnectionFactory";
import {
  acquirePullRequestEnvironmentLeaseConnections as acquirePullRequestEnvironmentConnectionsInternal,
  resetPullRequestEnvironmentLeasesForTests,
  type PullRequestEnvironmentLease,
} from "./pullRequestEnvironmentLease";
import {
  addConnectedSavedEnvironmentRecord as addConnectedSavedEnvironmentInternal,
  addSavedEnvironmentRecord as addSavedEnvironmentInternal,
  disconnectSavedEnvironment as disconnectSavedEnvironmentInternal,
  reconnectSavedEnvironmentRecord as reconnectSavedEnvironmentInternal,
  removeSavedEnvironmentRecord as removeSavedEnvironmentInternal,
  type SavedEnvironmentOperationDependencies,
} from "./savedEnvironmentOperations";

import {
  createRelayRuntimeEnvironmentConnection as ensureRelayRuntimeEnvironmentConnectionInternal,
  resetRelayRuntimeConnectionForTests,
} from "./relayRuntimeConnection";
import {
  runtimeServiceTestSurface,
  withCmmdRuntimeMetadataTimeout,
} from "./runtimeBootstrapPolicy";

export { runtimeServiceTestSurface };
import {
  setRuntimeConnected,
  setRuntimeConnecting,
  setRuntimeDisconnected,
  setRuntimeError,
} from "./runtimeConnectionStatus";

import {
  createSavedEnvironmentConnection,
  type SavedEnvironmentConnectionOptions,
} from "./savedEnvironmentConnection";
import {
  SavedEnvironmentConnectionRegistry,
  type SavedEnvironmentAcquisitionRegistryExpectation,
} from "./savedEnvironmentConnectionRegistry";
import {
  applyEnvironmentThreadDetailEvent,
  shouldApplyTerminalEvent,
} from "./environmentEventApplication";
import { ensureCmmdRuntimeSavedEnvironmentConnection as bootstrapCmmdRuntimeConnection } from "./cmmdRuntimeBootstrap";
import { createEnvironmentConnectionHandlers } from "./environmentConnectionHandlers";

export { shouldApplyTerminalEvent };
import { configureThreadDetailSubscriptionDependencies } from "./threadDetailSubscriptionDependencies";
import { configureEnvironmentEventDependencies } from "./environmentEventDependencies";
import { threadDetailSubscriptions } from "./threadDetailSubscriptionState";
import { disposeThreadDetailSubscriptionByKey } from "./threadDetailLivenessService";
import {
  disposeThreadDetailSubscriptionsForEnvironment,
  retainThreadDetailSubscription,
  retireEnvironmentId,
} from "./threadDetailRetentionService";

export { retainThreadDetailSubscription };

const environmentConnections = new Map<EnvironmentId, EnvironmentConnection>();
const savedEnvironmentConnectionRegistry = new SavedEnvironmentConnectionRegistry(
  (environmentId) => getSavedEnvironmentRecord(environmentId),
  savedCloud.hasSameSavedEnvironmentConnectionTarget,
);
const environmentConnectionListeners = new Set<() => void>();
const lifecycle = createEnvironmentServiceLifecycle({
  createPrimaryEnvironmentConnection,
  syncSavedEnvironmentConnections,
});

function notifyProviderInvalidation(): void {
  lifecycle.notifyProviderInvalidation();
}

function emitEnvironmentConnectionRegistryChange() {
  for (const listener of environmentConnectionListeners) {
    listener();
  }
}

function registerConnection(connection: EnvironmentConnection): EnvironmentConnection {
  const existing = environmentConnections.get(connection.environmentId);
  if (existing && existing !== connection) {
    throw new Error(`Environment ${connection.environmentId} already has an active connection.`);
  }
  environmentConnections.set(connection.environmentId, connection);
  emitEnvironmentConnectionRegistryChange();
  return connection;
}

/** Drops a connection. Retained thread subscriptions detach and wait for the next one. */
async function removeConnection(environmentId: EnvironmentId): Promise<boolean> {
  const connection = environmentConnections.get(environmentId);
  if (!connection) {
    return false;
  }

  environmentConnections.delete(environmentId);
  unregisterCloudRuntimeCredentialProvider(environmentId);
  disposeThreadDetailSubscriptionsForEnvironment(environmentId);
  emitEnvironmentConnectionRegistryChange();
  await connection.dispose();
  return true;
}

function createPrimaryEnvironmentConnection(): EnvironmentConnection {
  return createPrimaryEnvironmentConnectionInternal({
    readConnection: readEnvironmentConnection,
    registerConnection,
  });
}

async function ensureSavedEnvironmentConnection(
  record: SavedEnvironmentRecord,
  options?: SavedEnvironmentConnectionOptions,
): Promise<EnvironmentConnection> {
  const existing = environmentConnections.get(record.environmentId);
  if (existing) return existing;
  return savedEnvironmentConnectionRegistry.ensure(record, (currentRecord, generation) =>
    createSavedEnvironmentConnection(
      currentRecord,
      generation,
      {
        findConnection: (environmentId) => environmentConnections.get(environmentId),
        registerConnection,
        canCommit: savedEnvironmentConnectionRegistry.isCurrent.bind(
          savedEnvironmentConnectionRegistry,
        ),
        createHandlers: createEnvironmentConnectionHandlers,
        withMetadataTimeout: withCmmdRuntimeMetadataTimeout,
        setConnecting: setRuntimeConnecting,
        setConnected: setRuntimeConnected,
        setDisconnected: setRuntimeDisconnected,
        setError: setRuntimeError,
      },
      options,
    ),
  );
}

async function syncSavedEnvironmentConnections(
  records: ReadonlyArray<SavedEnvironmentRecord>,
): Promise<void> {
  await savedEnvironmentConnectionRegistry.sync({
    records,
    connectedSavedEnvironmentIds: [...environmentConnections.values()]
      .filter((connection) => connection.kind === "saved")
      .map((connection) => connection.environmentId),
    disconnect: disconnectSavedEnvironment,
  });
}

export function subscribeEnvironmentConnections(listener: () => void): () => void {
  environmentConnectionListeners.add(listener);
  return () => {
    environmentConnectionListeners.delete(listener);
  };
}

export function readEnvironmentConnection(
  environmentId: EnvironmentId,
): EnvironmentConnection | null {
  return environmentConnections.get(environmentId) ?? null;
}

export function ensureRelayRuntimeEnvironmentConnection(input: {
  readonly runtimeId: string;
  readonly label: string;
}): Promise<EnvironmentConnection> {
  return ensureRelayRuntimeEnvironmentConnectionInternal(input, {
    readConnection: readEnvironmentConnection,
    registerConnection,
  });
}

export function requireEnvironmentConnection(environmentId: EnvironmentId): EnvironmentConnection {
  const connection = readEnvironmentConnection(environmentId);
  if (!connection) {
    throw new Error(`No websocket client registered for environment ${environmentId}.`);
  }
  return connection;
}

export function getPrimaryEnvironmentConnection(): EnvironmentConnection {
  return createPrimaryEnvironmentConnection();
}

export async function ensureDesktopLocalEnvironmentConnection(): Promise<EnvironmentConnection> {
  return ensureDesktopLocalEnvironmentConnectionInternal({
    readConnection: readEnvironmentConnection,
    registerConnection,
  });
}

function savedEnvironmentOperationDependencies(): SavedEnvironmentOperationDependencies {
  return {
    readConnection: readEnvironmentConnection,
    removeConnection,
    ensureSavedEnvironmentConnection,
    ensureCmmdRuntimeSavedEnvironmentConnection,
    invalidate: (environmentId) => savedEnvironmentConnectionRegistry.invalidate(environmentId),
    retireEnvironmentId,
  };
}

async function disconnectSavedEnvironment(environmentId: EnvironmentId): Promise<void> {
  await disconnectSavedEnvironmentInternal(environmentId, savedEnvironmentOperationDependencies());
}

export async function reconnectSavedEnvironment(environmentId: EnvironmentId): Promise<void> {
  await reconnectSavedEnvironmentInternal(environmentId, savedEnvironmentOperationDependencies());
}

export type { PullRequestEnvironmentLease } from "./pullRequestEnvironmentLease";

export async function acquirePullRequestEnvironmentConnections(): Promise<PullRequestEnvironmentLease> {
  return acquirePullRequestEnvironmentConnectionsInternal({
    readConnection: readEnvironmentConnection,
    ensureCmmdRuntimeSavedEnvironmentConnection,
  });
}

export async function removeSavedEnvironment(environmentId: EnvironmentId): Promise<void> {
  await removeSavedEnvironmentInternal(environmentId, savedEnvironmentOperationDependencies());
}

export async function addSavedEnvironment(
  input: Parameters<typeof addSavedEnvironmentInternal>[0],
): Promise<SavedEnvironmentRecord> {
  return addSavedEnvironmentInternal(input, savedEnvironmentOperationDependencies());
}

export async function addConnectedSavedEnvironment(
  input: Parameters<typeof addConnectedSavedEnvironmentInternal>[0],
): Promise<SavedEnvironmentRecord> {
  return addConnectedSavedEnvironmentInternal(input, savedEnvironmentOperationDependencies());
}

type ConnectedSavedEnvironmentBootstrapRetry = savedCloud.ConnectedSavedEnvironmentBootstrapRetry;

async function ensureCmmdRuntimeSavedEnvironmentConnection(
  record: SavedEnvironmentRecord,
  options?: {
    readonly bearerToken?: string;
    readonly signal?: AbortSignal;
    readonly onBootstrapRetry?: (progress: ConnectedSavedEnvironmentBootstrapRetry) => void;
    readonly registryExpectation?: SavedEnvironmentAcquisitionRegistryExpectation;
  },
): Promise<EnvironmentConnection> {
  return bootstrapCmmdRuntimeConnection(record, options, {
    ensureSavedEnvironmentConnection,
    getGeneration: (environmentId) =>
      savedEnvironmentConnectionRegistry.generationFor(environmentId),
    registry: savedEnvironmentConnectionRegistry,
    removeConnection,
    setRuntimeError,
  });
}

export async function ensureEnvironmentConnectionBootstrapped(
  environmentId: EnvironmentId,
): Promise<void> {
  await environmentConnections.get(environmentId)?.ensureBootstrapped();
}

export async function ensureEnvironmentProviderAuthority(
  environmentId: EnvironmentId,
): Promise<void> {
  const connection = environmentConnections.get(environmentId);
  if (connection?.ensureProviderAuthority) {
    await connection.ensureProviderAuthority();
    return;
  }
  // Cloud classification alone no longer implies a renewing provider exists: a
  // durable Environment connected through the static bearer path (no runtime id
  // was resolvable at connect time) never gets one, the same as a non-cloud
  // record, so there is no authority to ensure and this is a no-op.
  //
  // A connection that DID register a renewing provider and still has no
  // authority to ensure is the FOR-2394 failure, and still fails loudly rather
  // than sending on unrenewed credentials. The registry, not the record, is the
  // discriminator: the catalog can hand the Environment a Sprite between connect
  // and send, and re-deriving from the record would then throw for a connection
  // that legitimately took the static path.
  if (hasCloudRuntimeCredentialProvider(environmentId)) {
    throw new Error("Cloud runtime provider authority is not available.");
  }
}

export function startEnvironmentConnectionService(queryClient: QueryClient): () => void {
  return lifecycle.start(queryClient);
}

export async function resetEnvironmentServiceForTests(): Promise<void> {
  lifecycle.stopActiveService();
  resetRelayRuntimeConnectionForTests();
  resetPullRequestEnvironmentLeasesForTests();
  savedEnvironmentConnectionRegistry.clear();
  for (const key of Array.from(threadDetailSubscriptions.keys())) {
    disposeThreadDetailSubscriptionByKey(key);
  }
  await Promise.all(
    [...environmentConnections.keys()].map((environmentId) => removeConnection(environmentId)),
  );
}

configureThreadDetailSubscriptionDependencies({
  applyEvent: applyEnvironmentThreadDetailEvent,
  readConnection: readEnvironmentConnection,
  subscribeConnections: subscribeEnvironmentConnections,
});

configureEnvironmentEventDependencies({
  notifyProviderInvalidation,
  readQueryClient: () => lifecycle.readActiveService()?.queryClient ?? null,
  setRuntimeError,
});
